/**
 * Centralized Color Palette & Color Schemes for Autobet
 * Defines primary, secondary, status, neutral, gradient, chart, and dashboard theme colors.
 */

export const colors = {
  // Home Page Specific Colors
  home: {
    brandRed: "#ef334f",
    brandDark: "#17333d",
    grayBlue: "#667783",
    cardFlat: "#f7fafb",
    navyDark: "#172033",
    navyDeep: "#050914",
    textMuted: "#8b9bb4",
    textLight: "#aeb8c9",
  },

  // Alpha / Opacity Presets
  alpha: {
    white: {
      5: "rgba(255, 255, 255, 0.05)",
      12: "rgba(255, 255, 255, 0.12)",
      30: "rgba(255, 255, 255, 0.3)",
      40: "rgba(255, 255, 255, 0.4)",
      50: "rgba(255, 255, 255, 0.5)",
      55: "rgba(255, 255, 255, 0.55)",
      60: "rgba(255, 255, 255, 0.6)",
      68: "rgba(255, 255, 255, 0.68)",
      70: "rgba(255, 255, 255, 0.7)",
      75: "rgba(255, 255, 255, 0.75)",
      80: "rgba(255, 255, 255, 0.8)",
      82: "rgba(255, 255, 255, 0.82)",
      88: "rgba(255, 255, 255, 0.88)",
      90: "rgba(255, 255, 255, 0.9)",
      95: "rgba(255, 255, 255, 0.95)",
      97: "rgba(255, 255, 255, 0.97)",
    },
    black: {
      4: "rgba(0, 0, 0, 0.04)",
      10: "rgba(0, 0, 0, 0.1)",
      20: "rgba(0, 0, 0, 0.2)",
      25: "rgba(0, 0, 0, 0.25)",
      30: "rgba(0, 0, 0, 0.3)",
      60: "rgba(0, 0, 0, 0.6)",
      75: "rgba(0, 0, 0, 0.75)",
      85: "rgba(0, 0, 0, 0.85)",
    },
    navy: {
      50: "rgba(23, 32, 51, 0.5)",
      52: "rgba(23, 32, 51, 0.52)",
      55: "rgba(23, 32, 51, 0.55)",
      58: "rgba(23, 32, 51, 0.58)",
    },
    cream: {
      5: "rgba(248, 247, 243, 0.05)",
      30: "rgba(248, 247, 243, 0.3)",
      88: "rgba(248, 247, 243, 0.88)",
      96: "rgba(248, 247, 243, 0.96)",
    },
    red: {
      12: "rgba(239, 68, 68, 0.12)",
      15: "rgba(239, 68, 68, 0.15)",
      30: "rgba(239, 68, 68, 0.3)",
    },
    primary: {
      12: "rgba(37, 99, 235, 0.12)",
      18: "rgba(37, 99, 235, 0.18)",
      28: "rgba(37, 99, 235, 0.28)",
      30: "rgba(37, 99, 235, 0.3)",
    },
    navyDeep: {
      18: "rgba(5, 9, 20, 0.18)",
    }
  },

  // Brand Main Colors
  primary: {
    main: "#2563eb", // modern brand blue
    light: "#3b82f6",
    dark: "#1d4ed8",
    contrastText: "#ffffff",
    lighter: "#eff6ff",
    subtle: "rgba(37, 99, 235, 0.12)",
    accent: "#fc9d2e",
  },
  secondary: {
    main: "#0f172a", // slate 900
    light: "#1e293b",
    dark: "#0d1527",
    contrastText: "#ffffff",
    lighter: "#f8fafc",
  },

  // Slate Scale
  slate: {
    50: "#f8fafc",
    100: "#f1f5f9",
    200: "#e2e8f0",
    300: "#cbd5e1",
    400: "#94a3b8",
    500: "#64748b",
    600: "#475569",
    700: "#334155",
    800: "#1e293b",
    900: "#0f172a",
    950: "#0d1527",
  },

  // Blue Scale
  blue: {
    50: "#eff6ff",
    100: "#dbeafe",
    200: "#bfdbfe",
    300: "#93c5fd",
    400: "#60a5fa",
    500: "#3b82f6",
    600: "#2563eb",
    700: "#1d4ed8",
    800: "#1e40af",
    900: "#1e3a8a",
  },

  // Emerald / Green Scale
  emerald: {
    50: "#ecfdf5",
    100: "#d1fae5",
    200: "#a7f3d0",
    300: "#6ee7b7",
    400: "#34d399",
    500: "#10b981",
    600: "#059669",
    700: "#047857",
    800: "#065f46",
    900: "#064e3b",
  },

  // Amber Scale
  amber: {
    50: "#fffbeb",
    100: "#fef3c7",
    200: "#fde68a",
    300: "#fcd34d",
    400: "#fbbf24",
    500: "#f59e0b",
    600: "#d97706",
    700: "#b45309",
    800: "#92400e",
    900: "#78350f",
  },

  // Red / Rose Scale
  red: {
    50: "#fef2f2",
    100: "#fee2e2",
    200: "#fecaca",
    300: "#fca5a5",
    400: "#f87171",
    500: "#ef4444",
    600: "#dc2626",
    700: "#b91c1c",
    800: "#991b1b",
    900: "#7f1d1d",
  },

  // Purple / Violet Scale
  purple: {
    50: "#faf5ff",
    100: "#f3e8ff",
    200: "#e9d5ff",
    300: "#d8b4fe",
    400: "#c084fc",
    500: "#8b5cf6",
    600: "#7c3aed",
    700: "#6d28d9",
    800: "#5b21b6",
    900: "#4c1d95",
  },

  // Cyan Scale
  cyan: {
    50: "#ecfeff",
    100: "#cffafe",
    200: "#a5f3fc",
    300: "#67e8f9",
    400: "#22d3ee",
    500: "#06b6d4",
    600: "#0891b2",
    700: "#0e7490",
    800: "#155e75",
    900: "#164e63",
  },

  // Teal Scale
  teal: {
    50: "#f0fdfa",
    100: "#ccfbf1",
    200: "#99f6e4",
    300: "#5eead4",
    400: "#2dd4bf",
    500: "#14b8a6",
    600: "#0d9488",
    700: "#0f766e",
    800: "#115e59",
    900: "#134e4a",
  },

  // Rose / Crimson Scale
  rose: {
    50: "#fff1f2",
    100: "#ffe4e6",
    200: "#fecdd3",
    300: "#fda4af",
    400: "#fb7185",
    500: "#f43f5e",
    600: "#e11d48",
    700: "#be123c",
    800: "#9f1239",
    900: "#881337",
  },

  // Indigo Scale
  indigo: {
    50: "#eef2ff",
    100: "#e0e7ff",
    200: "#c7d2fe",
    300: "#a5b4fc",
    400: "#818cf8",
    500: "#6366f1",
    600: "#4f46e5",
    700: "#4338ca",
    800: "#3730a3",
    900: "#312e81",
  },

  // Theme Neutral / Common Colors
  neutral: {
    white: "#ffffff",
    black: "#000000",
    light: "#feffff",
    lightGrey: "#94a3b8",
    grayLight: "#f8fafc",
    border: "#e2e8f0",
    borderHover: "#cbd5e1",
    divider: "#f1f5f9",
    textBoldLight: "#334155",
    textTitle: "#0f172a",
    textMuted: "#64748b",
  },

  // Semantic Status Colors
  success: {
    main: "#10b981",
    light: "#34d399",
    dark: "#059669",
    contrastText: "#ffffff",
    background: "#ecfdf5",
  },
  warning: {
    main: "#f59e0b",
    light: "#fbbf24",
    dark: "#d97706",
    contrastText: "#ffffff",
    background: "#fffbeb",
  },
  error: {
    main: "#ef4444",
    light: "#f87171",
    dark: "#dc2626",
    contrastText: "#ffffff",
    background: "#fef2f2",
  },
  info: {
    main: "#2563eb",
    light: "#60a5fa",
    dark: "#1d4ed8",
    contrastText: "#ffffff",
    background: "#eff6ff",
  },

  // Chart Sparkline Palette
  charts: {
    blue: "#3b82f6",
    green: "#10b981",
    purple: "#8b5cf6",
    red: "#ef4444",
    amber: "#f59e0b",
    cyan: "#06b6d4",
    teal: "#0d9488",
  },

  // Dashboard Theme Specific Tokens
  dashboard: {
    canvas: "#f4f6f9",
    cardBg: "#ffffff",
    cardBorder: "#e2e8f0",
    cardBorderHover: "#cbd5e1",
    divider: "#f1f5f9",
    tableHeader: "#f8fafc",
    tableRowHover: "#f8fafc",
    tableBorder: "#f1f5f9",
    title: "#0f172a",
    subtitle: "#64748b",
    label: "#475569",
    value: "#0f172a",
    trendPositive: "#10b981",
    trendNegative: "#ef4444",
    noticeBanner: {
      background: "linear-gradient(90deg, #eff6ff 0%, #f0fdf4 100%)",
      border: "#bfdbfe",
      text: "#1e3a8a",
      icon: "#2563eb",
      buttonBg: "#2563eb",
      buttonHover: "#1d4ed8",
      buttonText: "#ffffff",
    },
    alertBox: {
      background: "#fffbeb",
      border: "#fef3c7",
      icon: "#d97706",
      title: "#b45309",
      text: "#92400e",
    },
    status: {
      activeBg: "#ecfdf5",
      activeText: "#059669",
      endedBg: "#f1f5f9",
      endedText: "#475569",
      pendingBg: "#fffbeb",
      pendingText: "#d97706",
      warningBg: "#fef3c7",
      warningText: "#b45309",
      successBg: "#ecfdf5",
      successText: "#059669",
      dangerBg: "#fef2f2",
      dangerText: "#ef4444",
    },
  },

  // Sidebar Dark Navigation Tokens
  sidebar: {
    bg: "#0d1527",
    border: "#1e293b",
    text: "#e2e8f0",
    textHover: "#ffffff",
    textActive: "#ffffff",
    itemActiveBg: "#2563eb",
    itemActiveHover: "#1d4ed8",
    itemHoverBg: "rgba(255, 255, 255, 0.08)",
    icon: "#94a3b8",
    iconActive: "#ffffff",
    badgeBg: "#ef4444",
    badgeText: "#ffffff",
    quarterBoxBg: "rgba(0, 0, 0, 0.2)",
    quarterLabel: "#94a3b8",
    quarterTitle: "#ffffff",
    quarterSub: "#cbd5e1",
    brandEmblem: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
  },

  // Top Nav Header Tokens
  topNav: {
    bg: "#0d1527",
    border: "#1e293b",
    text: "#ffffff",
    subtext: "#94a3b8",
    iconBtnBg: "rgba(255, 255, 255, 0.05)",
    iconBtnHover: "rgba(255, 255, 255, 0.1)",
    badge: "#ef4444",
    avatarBg: "#2563eb",
  },

  // Glassmorphic Navbar for main site
  glassNav: {
    bg: "rgba(248, 250, 255, 0.85)", // white with slight blue tint
    border: "rgba(37, 99, 235, 0.15)", // subtle blue border
    text: "#0f172a",
    hover: "#2563eb",
  },

  // Bottom Dark Stats Ribbon Tokens
  ribbon: {
    bg: "#0d1527",
    border: "#1e293b",
    label: "#94a3b8",
    value: "#ffffff",
    deltaPositive: "#10b981",
    deltaPositiveSub: "#34d399",
    deltaNegative: "#ef4444",
    deltaNegativeSub: "#f87171",
    divider: "rgba(255, 255, 255, 0.08)",
  },

  // Text Colors
  text: {
    primary: "#0f172a",
    secondary: "#64748b",
    disabled: "#94a3b8",
    hint: "#94a3b8",
    muted: "#64748b",
    white: "#ffffff",
    title: "#0f172a",
  },

  // Background Colors
  background: {
    default: "#f4f6f9",
    paper: "#ffffff",
    dark: "#0d1527",
    subtle: "#f8fafc",
    light: "#f8fafc",
    card: "#ffffff",
  },

  // Action / State Colors
  action: {
    active: "#2563eb",
    hover: "rgba(37, 99, 235, 0.08)",
    selected: "rgba(37, 99, 235, 0.16)",
    disabled: "rgba(15, 23, 42, 0.26)",
    disabledBackground: "rgba(15, 23, 42, 0.12)",
    focus: "rgba(37, 99, 235, 0.24)",
  },

  // Overlays & Transparencies
  overlay: {
    dark: "rgba(13, 21, 39, 0.7)",
    light: "rgba(255, 255, 255, 0.8)",
  },

  // Gradients
  gradients: {
    primary: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
    notice: "linear-gradient(90deg, #eff6ff 0%, #f0fdf4 100%)",
    dark: "linear-gradient(135deg, #0d1527 0%, #1e293b 100%)",
    redEmblem: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
    cardSurface: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
    becomePartner: "linear-gradient(115deg, #07102d 0%, #0d1b4d 45%, #172554 70%, #3b0d35 100%)",
  },

  // Centralized Lottery Game Type Themes (Single source of truth for lottery branding)
  lottery: {
    // 0: SpinBET
    spinbet: {
      id: 0,
      name: "SpinBET",
      color: "#2563eb", // blue[600]
      lightColor: "#60a5fa",
      darkColor: "#1d4ed8",
      bgColor: "#eff6ff", // blue[50]
      borderColor: "#bfdbfe",
      gradient: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
      glow: "rgba(37, 99, 235, 0.35)",
      boxShadow: "0 4px 14px rgba(37, 99, 235, 0.4)",
      hoverBorder: "#60a5fa",
    },
    // 1: MineBET
    minebet: {
      id: 1,
      name: "MineBET",
      color: "#7c3aed", // purple[600]
      lightColor: "#a855f7",
      darkColor: "#6d28d9",
      bgColor: "#faf5ff", // purple[50]
      borderColor: "#e9d5ff",
      gradient: "linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)",
      glow: "rgba(124, 58, 237, 0.35)",
      boxShadow: "0 4px 14px rgba(124, 58, 237, 0.4)",
      hoverBorder: "#c084fc",
    },
    // 2: MegaBET (Electric Indigo Jackpot)
    megabet: {
      id: 2,
      name: "MegaBET",
      color: "#4f46e5", // indigo[600]
      lightColor: "#818cf8", // indigo[400]
      darkColor: "#3730a3", // indigo[800]
      bgColor: "#eef2ff", // indigo[50]
      borderColor: "#c7d2fe", // indigo[200]
      gradient: "linear-gradient(135deg, #6366f1 0%, #4f46e5 50%, #3730a3 100%)",
      glow: "rgba(79, 70, 229, 0.35)",
      boxShadow: "0 4px 14px rgba(79, 70, 229, 0.4)",
      hoverBorder: "#818cf8", // indigo[400]
    },
    // 3: RocketBET
    rocketbet: {
      id: 3,
      name: "RocketBET",
      color: "#d97706", // amber[600]
      lightColor: "#f59e0b",
      darkColor: "#b45309",
      bgColor: "#fffbeb", // amber[50]
      borderColor: "#fde68a",
      gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
      glow: "rgba(217, 119, 6, 0.35)",
      boxShadow: "0 4px 14px rgba(217, 119, 6, 0.4)",
      hoverBorder: "#fbbf24",
    },
    // 4: 3-SlotBET
    threeslot: {
      id: 4,
      name: "3-SlotBET",
      color: "#8b5cf6", // purple[500]
      lightColor: "#a78bfa",
      darkColor: "#6d28d9",
      bgColor: "#faf5ff", // purple[50]
      borderColor: "#e9d5ff",
      gradient: "linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)",
      glow: "rgba(139, 92, 246, 0.35)",
      boxShadow: "0 4px 14px rgba(139, 92, 246, 0.4)",
      hoverBorder: "#c084fc",
    },
    // 5: 5-SlotBET
    fiveslot: {
      id: 5,
      name: "5-SlotBET",
      color: "#2563eb", // blue[600]
      lightColor: "#60a5fa",
      darkColor: "#1d4ed8",
      bgColor: "#eff6ff", // blue[50]
      borderColor: "#bfdbfe",
      gradient: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
      glow: "rgba(37, 99, 235, 0.35)",
      boxShadow: "0 4px 14px rgba(37, 99, 235, 0.4)",
      hoverBorder: "#60a5fa",
    },
  },
};

/**
 * Numeric map of Lottery Color Themes by Game Type ID
 * 
 */
export const LOTTERY_COLOR_THEMES = {
  0: colors.lottery.spinbet,
  1: colors.lottery.minebet,
  2: colors.lottery.megabet,
  3: colors.lottery.rocketbet,
  4: colors.lottery.threeslot,
  5: colors.lottery.fiveslot,
};

// Named direct exports for convenience
export const SPINBET_COLOR_THEME = colors.lottery.spinbet;
export const MINEBET_COLOR_THEME = colors.lottery.minebet;
export const MEGABET_COLOR_THEME = colors.lottery.megabet;
export const ROCKETBET_COLOR_THEME = colors.lottery.rocketbet;
export const THREESLOT_COLOR_THEME = colors.lottery.threeslot;
export const FIVESLOT_COLOR_THEME = colors.lottery.fiveslot;

/**
 * Helper to safely get the lottery theme by type ID or name
 */
export const getLotteryColorTheme = (typeIdOrName) => {
  if (typeof typeIdOrName === "string") {
    const key = typeIdOrName.toLowerCase().trim();
    if (key === "spinbet" || key === "0") return colors.lottery.spinbet;
    if (key === "minebet" || key === "1") return colors.lottery.minebet;
    if (key === "megabet" || key === "mrl" || key === "2") return colors.lottery.megabet;
    if (key === "rocketbet" || key === "missile" || key === "3") return colors.lottery.rocketbet;
    if (key === "threeslot" || key === "3slot" || key === "pokies" || key === "4") return colors.lottery.threeslot;
    if (key === "fiveslot" || key === "5slot" || key === "5") return colors.lottery.fiveslot;
  }
  const numericId = Number(typeIdOrName !== undefined && typeIdOrName !== null ? typeIdOrName : 0);
  return LOTTERY_COLOR_THEMES[numericId] || LOTTERY_COLOR_THEMES[0];
};

export default colors;
